import { useState, useEffect } from "react";
import { FaCheck, FaEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ExportOptions from "../ExportOptions";
import DynamicSelect from "../DynamicSelect";
import TableSkeleton from "../TableSkeleton";
import ConfirmActionModal from "../ConfirmDeleteModal";
import PageHeading from "../PageHeading";

// Modal for setting wastage
const WastageModal = ({ isOpen, onClose, onSave, assets, dropdownAssets, wastageRecord, selectedAssetId, fetchAssets }) => {
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [wastage, setWastage] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Prepare options for DynamicSelect
  const assetOptions = dropdownAssets.map((a) => ({
    value: a.id,
    label: a.assetName,
  }));

  useEffect(() => {
    if (isOpen) {
      if (wastageRecord && selectedAssetId) {
        // Edit mode: Populate fields with total wastage and latest note
        const asset = assets.find((a) => a.id === selectedAssetId);
        const dropdownAsset = dropdownAssets.find((a) => a.id === selectedAssetId);
        if (dropdownAsset) {
          setSelectedAsset({ value: dropdownAsset.id, label: dropdownAsset.assetName });
          setWastage(asset && asset.wastage !== undefined ? asset.wastage.toString() : "0");
        }
        setNote(wastageRecord.note || "");
        const dropdownAssetForPrice = dropdownAssets.find((a) => a.id === selectedAssetId);
        const priceToShow =
          wastageRecord.unitPrice != null && wastageRecord.unitPrice > 0
            ? wastageRecord.unitPrice
            : (dropdownAssetForPrice?.unitCost ?? "");
        setUnitPrice(priceToShow !== "" ? String(priceToShow) : "");
      } else {
        setSelectedAsset(null);
        setWastage("");
        setUnitPrice("");
        setNote("");
      }
      setErrors({});
    }
  }, [isOpen, wastageRecord, selectedAssetId, assets, dropdownAssets]);

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};
    if (!selectedAsset) {
      newErrors.assetName = "Asset Name is required";
    }
    if (!wastage.trim()) {
      newErrors.wastage = "Wastage is required";
    } else if (!/^\d+$/.test(wastage) || parseInt(wastage) < 0) {
      newErrors.wastage = "Wastage must be a non-negative integer";
    } else {
      const currentAsset = assets.find((a) => a.id === selectedAsset?.value);
      if (currentAsset && parseInt(wastage) > currentAsset.assetValue + (currentAsset.wastage || 0)) {
        newErrors.wastage = "Wastage cannot exceed available stock plus previous wastage";
      }
    }
    if (!note.trim()) {
      newErrors.note = "Note is required";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the errors in the form", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const endpoint = wastageRecord
        ? `https://api.muktihospital.com/api/wastage/edit/${wastageRecord.id}`
        : "https://api.muktihospital.com/api/wastage/add";
      const method = wastageRecord ? "PUT" : "POST";

      const payload = {
        assetId: selectedAsset.value,
        wastageValue: parseInt(wastage),
        unitPrice: unitPrice !== "" ? parseFloat(unitPrice) : undefined,
        note: note.trim(),
      };

      const response = await fetch(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
        },
        body: JSON.stringify(payload),
      });

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server returned non-JSON response");
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${wastageRecord ? "update" : "record"} wastage`);
      }

      const { wastage: newWastage, wastageDetails } = await response.json();
      onSave(selectedAsset.value, parseInt(wastage), wastageDetails.newAssetValue);
      await fetchAssets(); // Refresh assets to ensure table is updated
      toast.success(`Wastage ${wastageRecord ? "updated" : "recorded"} successfully!`, {
        position: "top-right",
        autoClose: 3000,
      });
      setSelectedAsset(null);
      setWastage("");
      setNote("");
      setErrors({});
      onClose();
    } catch (error) {
      console.error(`Error ${wastageRecord ? "updating" : "saving"} wastage:`, error);
      toast.error(error.message || `Error ${wastageRecord ? "updating" : "saving"} wastage!`, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get current asset value for display
  const selectedAssetData = selectedAsset
    ? dropdownAssets.find((a) => a.id === selectedAsset.value)
    : null;
  const currentAssetValue = selectedAssetData?.quantity ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden p-2">
      <div className="bg-white rounded-lg shadow-lg w-96">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-700">
            {wastageRecord ? "Edit Wastage" : "Record Wastage"}
          </h2>
        </div>
        <div className="p-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Asset Name <span className="text-red-500">*</span>
          </label>
          <DynamicSelect
            options={assetOptions}
            onChange={(opt) => {
              setSelectedAsset(opt);
              const a = dropdownAssets.find((x) => x.id === opt?.value);
              setUnitPrice(a && a.unitCost != null ? String(a.unitCost) : "");
            }}
            value={selectedAsset}
            isSearchable={true}
            placeholder="Select Asset"
            controlBackgroundColor="#f7fafc"
            controlFontSize="14px"
            controlBorderColor={errors.assetName ? "#ef4444" : "#cbd5e0"}
            isDisabled={isLoading || !!wastageRecord}
          />
          {errors.assetName && (
            <span className="text-red-500 text-xs mt-1">{errors.assetName}</span>
          )}
          <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">
            Current Stock (Read Only)
          </label>
          <input
            type="text"
            value={currentAssetValue}
            readOnly
            className="w-full p-2 border rounded-md outline-none focus:ring-0 text-sm text-gray-700 bg-gray-100 cursor-not-allowed"
          />
          <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">
            Wastage <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={wastage}
            onChange={(e) => {
              setWastage(e.target.value);
              if (e.target.value.trim()) {
                setErrors((prev) => ({ ...prev, wastage: "" }));
              }
            }}
            className={`w-full p-2 border rounded-md outline-none focus:ring-0 text-sm text-gray-700 ${
              errors.wastage ? "border-red-500" : ""
            }`}
            placeholder="Wastage Amount"
            disabled={isLoading}
          />
          {errors.wastage && (
            <span className="text-red-500 text-xs mt-1">{errors.wastage}</span>
          )}
          <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">
            Unit Price
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={unitPrice}
            onChange={(e) => setUnitPrice(e.target.value)}
            className="w-full p-2 border rounded-md outline-none focus:ring-0 text-sm text-gray-700"
            placeholder="Auto-filled from asset, editable"
            disabled={isLoading}
          />
          <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600">Loss Amount</span>
            <span className="text-lg font-bold text-red-600">
              ৳ {(((parseFloat(wastage) || 0) * (parseFloat(unitPrice) || 0)) || 0).toFixed(2)}
            </span>
          </div>
          <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">
            Note <span className="text-red-500">*</span>
          </label>
          <textarea
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              if (e.target.value.trim()) {
                setErrors((prev) => ({ ...prev, note: "" }));
              }
            }}
            className={`w-full p-2 border rounded-md outline-none focus:ring-0 text-sm text-gray-700 ${
              errors.note ? "border-red-500" : ""
            }`}
            placeholder="Reason for wastage"
            disabled={isLoading}
            rows="3"
          />
          {errors.note && (
            <span className="text-red-500 text-xs mt-1">{errors.note}</span>
          )}
        </div>
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};
const Wastage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [wastageModalOpen, setWastageModalOpen] = useState(false);
  const [wastageRecordToEdit, setWastageRecordToEdit] = useState(null);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [dropdownAssets, setDropdownAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const itemsPerPage = 10;

  // Fetch assets and wastage from backend
  const fetchAssets = async () => {
  setLoading(true);
  try {
    const response = await fetch("https://api.muktihospital.com/api/wastage", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
      },
    });

    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      throw new Error("Server returned non-JSON response");
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch assets");
    }

    const { assets } = await response.json();
    // console.log("Fetched assets with wastage:", assets);

    // Fetch the latest wastage record for each asset to get the timestamp
    const assetsWithTimestamps = await Promise.all(
      assets.map(async (asset) => {
        const wastageRecord = await fetchLatestWastageRecord(asset.id);
        return {
          ...asset,
          latestWastageTimestamp: wastageRecord ? new Date(wastageRecord.createdAt) : new Date(0),
        };
      })
    );

    // Sort assets by the latest wastage timestamp (most recent first)
    const sortedAssets = assetsWithTimestamps.sort((a, b) => {
      return b.latestWastageTimestamp - a.latestWastageTimestamp; // Descending order
    });

    setAssets(sortedAssets);
  } catch (error) {
    console.error("Error fetching assets:", error);
    toast.error(error.message || "Error fetching assets!", {
      position: "top-right",
      autoClose: 3000,
    });
  } finally {
    setLoading(false);
  }
};

  // Fetch assets for WastageModal dropdown
  const fetchAssetsForDropdown = async () => {
    try {
      const response = await fetch("https://api.muktihospital.com/api/assets", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
        },
      });

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server returned non-JSON response");
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch assets for dropdown");
      }

      const { assets } = await response.json();
      const sortedAssets = assets.sort((a, b) => a.assetName.localeCompare(b.assetName));
      setDropdownAssets(sortedAssets);
      return sortedAssets;
    } catch (error) {
      console.error("Error fetching assets for dropdown:", error);
      toast.error(error.message || "Error fetching assets for dropdown!", {
        position: "top-right",
        autoClose: 3000,
      });
      return [];
    }
  };

  // Fetch latest wastage record for an asset (for note and other fields)
  const fetchLatestWastageRecord = async (assetId) => {
    try {
      const response = await fetch(`https://api.muktihospital.com/api/wastage/${assetId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
        },
      });

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server returned non-JSON response");
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to fetch wastage records for asset ${assetId}`);
      }

      const { wastageRecords } = await response.json();
      // console.log("Wastage records for asset", assetId, ":", wastageRecords);

      if (wastageRecords.length === 0) return null;

      const latestRecord = wastageRecords.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

      return {
        id: latestRecord.id,
        assetId,
        wastageValue: latestRecord.wastageValue,
        note: latestRecord.note || "",
        createdAt: latestRecord.createdAt,
      };
    } catch (error) {
      console.error(`Error fetching wastage record for asset ${assetId}:`, error);
      toast.error(error.message || "Error fetching wastage record!", {
        position: "top-right",
        autoClose: 3000,
      });
      return null;
    }
  };

  // Fetch assets on component mount
  useEffect(() => {
    fetchAssets();
  }, []);

  const filteredAssets = assets.filter((asset) =>
    asset.assetName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredAssets.length / itemsPerPage);

  const currentData = filteredAssets
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    .map((asset, index) => ({
      ...asset,
      displayId: (currentPage - 1) * itemsPerPage + index + 1,
    }));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(currentData.map((asset) => asset.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (assetId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(assetId)
        ? prevSelectedRows.filter((id) => id !== assetId)
        : [...prevSelectedRows, assetId]
    );
  };

  const handleDelete = async (assetIds) => {
    const idsToDelete = Array.isArray(assetIds) ? assetIds : [assetIds];
    setLoading(true);
    try {
      for (const assetId of idsToDelete) {
        const wastageRecord = await fetchLatestWastageRecord(assetId);
        if (!wastageRecord) {
          throw new Error(`No wastage record found for asset ${assetId}`);
        }

        const response = await fetch(`https://api.muktihospital.com/api/wastage/delete/${wastageRecord.id}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
        });

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error("Server returned non-JSON response");
        }

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to delete wastage record for asset ${assetId}`);
        }

        const { newAssetValue } = await response.json();
        setAssets((prevAssets) =>
          prevAssets.map((asset) =>
            asset.id === assetId
              ? {
                  ...asset,
                  assetValue: newAssetValue,
                  wastage: 0,
                }
              : asset
          )
        );
      }

      await fetchAssets();
      setSelectedRows([]);
      toast.success(`${idsToDelete.length} wastage record(s) deleted successfully!`, {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error deleting wastage records:", error);
      toast.error(error.message || "Error deleting wastage record(s)!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
    }
  };

const handleSaveWastage = (assetId, wastageValue, newAssetValue) => {
  setAssets((prevAssets) =>
    prevAssets.map((asset) =>
      asset.id === assetId
        ? {
            ...asset,
            assetValue: newAssetValue,
            wastage: wastageRecordToEdit ? wastageValue : (asset.wastage || 0) + wastageValue,
          }
        : asset
    )
  );
  setWastageRecordToEdit(null);
  setSelectedAssetId(null);
};

  const handleEditWastage = async (assetId) => {
    try {
      await fetchAssetsForDropdown();
      const wastageRecord = await fetchLatestWastageRecord(assetId);
      if (wastageRecord) {
        setSelectedAssetId(assetId);
        setWastageRecordToEdit(wastageRecord);
        setWastageModalOpen(true);
      } else {
        toast.error("No wastage record found for this asset!", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error preparing edit wastage:", error);
      toast.error("Error preparing to edit wastage!", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

   const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Store Management" },
    { label: "Wastage" },
  ];

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = ["ID", "Asset Name", "Asset Value", "Wastage"];
  const dataMapper = (item, index) =>
    [
      item.displayId ||
        (index !== undefined
          ? (currentPage - 1) * itemsPerPage + index + 1
          : item.id),
      item.assetName || "",
      item.assetValue || 0,
      item.wastage || 0,
    ].join("\t");

  const skeletonColumns = [
    {
      width: "40px",
      height: "16px",
      className: "text-left print-hidden-in-print",
      skeletonClassName: "my-5 mx-3",
    },
    {
      width: "100px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "200px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "100px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "100px",
      height: "16px",
      className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "80px",
      height: "16px",
      className: "text-right p-3 print-hidden-in-print",
      skeletonClassName: "ml-auto",
    },
    {
      width: "80px",
      height: "16px",
      className: "text-right p-3 print-hidden-in-print",
      skeletonClassName: "ml-auto",
    },
  ];

  return (
    <div>
      <ToastContainer />
       <div className="print-hidden">
        <PageHeading title="Wastage" breadcrumbs={breadcrumbs} />
      </div>
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Wastage List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected wastage records"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by asset name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="flex items-center w-full justify-between gap-3">
              <button
                onClick={async () => {
                  await fetchAssetsForDropdown();
                  setSelectedAssetId(null);
                  setWastageRecordToEdit(null);
                  setWastageModalOpen(true);
                }}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              >
                + Add Wastage
              </button>
              <ExportOptions
                data={currentData}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Wastage"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="overflow-x-auto print-wrapper">
            {loading ? (
              <div className="print-hidden">
                <table className="min-w-[600px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]"></div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        ID
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Asset Name
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Stock (Qty)
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Wastage
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Total Loss
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <TableSkeleton
                      rowCount={itemsPerPage}
                      columns={skeletonColumns}
                      baseColor="#e5e7eb"
                      highlightColor="#f3f4f6"
                    />
                  </tbody>
                </table>
              </div>
            ) : filteredAssets.length === 0 ? (
              <div className="p-5 text-center text-gray-600 print-hidden">
                No assets found.
              </div>
            ) : (
              <div className="print-wrapper">
                <table className="min-w-[600px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]">
                          <input
                            type="checkbox"
                            id="agreement"
                            className="hidden peer print-hidden"
                            onChange={handleSelectAll}
                            checked={
                              currentData.length > 0 &&
                              currentData.every((item) =>
                                selectedRows.includes(item.id)
                              )
                            }
                            disabled={loading}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor="agreement"
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                          ></label>
                        </div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        ID
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Asset Name
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Stock (Qty)
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Wastage
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Total Loss
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((asset, index) => {
                      // console.log("Rendering asset:", asset);
                      return (
                        <tr key={asset.id} className="border-b hover:bg-gray-50">
                          <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                            <div className="relative left-3 -top-[3px]">
                              <input
                                type="checkbox"
                                id={`asset-${index}`}
                                className="hidden peer print-hidden"
                                checked={selectedRows.includes(asset.id)}
                                onChange={() => handleRowSelect(asset.id)}
                                disabled={loading}
                              />
                              <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                                <FaCheck className="text-slate-100" />
                              </span>
                              <label
                                htmlFor={`asset-${index}`}
                                className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                              ></label>
                            </div>
                          </td>
                          <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {asset.displayId}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {asset.assetName}
                          </td>
                          <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {asset.quantity ?? 0}
                          </td>
                          <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {asset.wastage || 0}
                          </td>
                          <td className="text-right font-inter font-semibold text-sm text-red-600 py-5 px-3">
                            {asset.totalLoss != null ? `৳ ${Number(asset.totalLoss).toFixed(2)}` : "৳ 0.00"}
                          </td>
                          <td className="p-3 text-right space-x-2 print-hidden-in-print">
                            <button
                              onClick={() => handleEditWastage(asset.id)}
                              className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                              disabled={loading}
                              title="Edit Wastage"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedRows([asset.id]);
                                setDeleteModalOpen(true);
                              }}
                              className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                              disabled={loading}
                              title="Delete Wastage"
                            >
                              <RiDeleteBin6Line />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {totalPages > 1 && (
            <div className="p-5 text-end print-hidden">
              <div className="inline-flex items-center justify-between w-full">
                <p className="text-sm text-gray-600">
                  Records: {currentPage * itemsPerPage - itemsPerPage + 1} to{" "}
                  {Math.min(
                    currentPage * itemsPerPage,
                    filteredAssets.length
                  )}{" "}
                  of {filteredAssets.length}
                </p>
                <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                  <li>
                    <button
                      className="table-pagination-item disabled:opacity-50"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <MdKeyboardArrowLeft />
                    </button>
                  </li>
                  {totalPages <= 10
                    ? [...Array(totalPages)].map((_, i) => (
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i + 1
                                ? "bg-M-primary-color text-white"
                                : ""
                            }`}
                            onClick={() => handlePageChange(i + 1)}
                          >
                            {i + 1}
                          </button>
                        </li>
                      ))
                    : (() => {
                        const pages = [];
                        const firstPages = 5;
                        const lastPages = 3;

                        for (
                          let i = 1;
                          i <= Math.min(firstPages, totalPages);
                          i++
                        ) {
                          pages.push(
                            <li key={i}>
                              <button
                                className={`table-pagination-item ${
                                  currentPage === i
                                    ? "bg-M-primary-color text-white"
                                    : ""
                                }`}
                                onClick={() => handlePageChange(i)}
                              >
                                {i}
                              </button>
                            </li>
                          );
                        }

                        if (totalPages > firstPages + lastPages) {
                          pages.push(
                            <li
                              key="ellipsis"
                              className="table-pagination-item"
                            >
                              ...
                            </li>
                          );
                        }

                        for (
                          let i = Math.max(
                            firstPages + 1,
                            totalPages - lastPages + 1
                          );
                          i <= totalPages;
                          i++
                        ) {
                          pages.push(
                            <li key={i}>
                              <button
                                className={`table-pagination-item ${
                                  currentPage === i
                                    ? "bg-M-primary-color text-white"
                                    : ""
                                }`}
                                onClick={() => handlePageChange(i)}
                              >
                                {i}
                              </button>
                            </li>
                          );
                        }

                        return pages;
                      })()}
                  <li>
                    <button
                      className="table-pagination-item"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <MdKeyboardArrowRight />
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="wastage record"
        title="Confirm Delete Wastage"
        message={
          Array.isArray(selectedRows) && selectedRows.length > 1
            ? `Do you really want to delete ${selectedRows.length} wastage record(s)?`
            : `Do you really want to delete this wastage record?`
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
      <WastageModal
        isOpen={wastageModalOpen}
        onClose={() => {
          setWastageRecordToEdit(null);
          setSelectedAssetId(null);
          setWastageModalOpen(false);
        }}
        onSave={handleSaveWastage}
        assets={assets}
        dropdownAssets={dropdownAssets}
        wastageRecord={wastageRecordToEdit}
        selectedAssetId={selectedAssetId}
        fetchAssets={fetchAssets}
      />
    </div>
  );
};
export default Wastage;